import React, { useEffect, useState, useCallback } from "react";

/**
 * The First Arrival Observatory — SONMI's first scientific instrument.
 *
 * A place where understanding is witnessed, not merely stored.
 * No AI. No pattern recognition. No summarization. Only faithful
 * observation — so that many arrivals, gathered over time, can be
 * looked at together.
 *
 * See /app/memory/ECOLOGY_OF_UNDERSTANDING.md — Study 1.
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/first-arrival`;

// The closed vocabulary for the third question. The first entry is
// intentionally empty — the field is optional, and no default answer
// should be presumed.
const CHANGE_OPTIONS = [
  { value: "", label: "— leave unanswered —" },
  { value: "Nothing yet", label: "Nothing yet" },
  { value: "How I see things", label: "How I see things" },
  { value: "A decision I made", label: "A decision I made" },
  { value: "Something I said", label: "Something I said" },
  { value: "Something I did", label: "Something I did" },
  { value: "I'm still discovering", label: "I'm still discovering" },
];

const EMPTY_DRAFT = {
  title: "",
  arrival: "",
  observation: "",
  change: "",
  tagsText: "",
};

function parseTags(text) {
  return (text || "")
    .split(",")
    .map((t) => t.trim())
    .filter(Boolean);
}

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return "";
  }
}

export default function FirstArrival() {
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [justSavedTitle, setJustSavedTitle] = useState(null);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState(EMPTY_DRAFT);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const r = await fetch(API);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setEntries(data);
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const canSave = draft.title.trim() && draft.arrival.trim() && !saving;

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      const r = await fetch(API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: draft.title.trim(),
          arrival: draft.arrival.trim(),
          observation: draft.observation.trim(),
          change: draft.change,
          tags: parseTags(draft.tagsText),
        }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const saved = await r.json();
      setJustSavedTitle(saved.title);
      setDraft(EMPTY_DRAFT);
      await load();
      setTimeout(() => setJustSavedTitle(null), 3200);
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setSaving(false);
    }
  };

  const beginEdit = (entry) => {
    setEditingId(entry.id);
    setEditDraft({
      title: entry.title,
      arrival: entry.arrival,
      observation: entry.observation || "",
      change: entry.change || "",
      tagsText: (entry.tags || []).join(", "),
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditDraft(EMPTY_DRAFT);
  };

  const saveEdit = async (id) => {
    if (!editDraft.title.trim() || !editDraft.arrival.trim()) return;
    setError(null);
    try {
      const r = await fetch(`${API}/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: editDraft.title.trim(),
          arrival: editDraft.arrival.trim(),
          observation: editDraft.observation.trim(),
          change: editDraft.change,
          tags: parseTags(editDraft.tagsText),
        }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      cancelEdit();
      await load();
    } catch (e) {
      setError(String(e.message || e));
    }
  };

  return (
    <div
      className="min-h-screen w-full"
      style={{ backgroundColor: "#FAFAF7", fontFamily: "'Outfit', sans-serif", color: "#44403c" }}
      data-testid="first-arrival-page"
    >
      {/* Study label — same quiet stamp as the physics studies. */}
      <div
        className="absolute top-6 left-8 text-[10px] uppercase tracking-[0.18em] text-stone-400"
        data-testid="page-label"
      >
        SONMI · The First Arrival Observatory · instrument v0.1
      </div>

      <div className="max-w-2xl mx-auto px-8 pt-28 pb-24">
        {/* Title + invitation. */}
        <h1
          className="text-stone-900"
          style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontWeight: 300,
            fontSize: "2.6rem",
            letterSpacing: "-0.01em",
            lineHeight: 1.15,
          }}
          data-testid="page-title"
        >
          The First Arrival Observatory
        </h1>
        <p
          className="mt-4 italic text-stone-500"
          style={{ fontSize: "1.05rem", lineHeight: 1.55 }}
          data-testid="page-invitation"
        >
          Tell me about a moment when something suddenly made sense.
        </p>

        {/* Compose. */}
        <section className="mt-16 space-y-8" data-testid="compose">
          <Field
            label="Title"
            testId="input-title"
            value={draft.title}
            onChange={(v) => setDraft({ ...draft, title: v })}
            placeholder="a phrase that names the moment"
          />
          <TextArea
            label="The Arrival"
            hint="the story exactly as lived, without interpretation"
            testId="input-arrival"
            value={draft.arrival}
            onChange={(v) => setDraft({ ...draft, arrival: v })}
            minRows={6}
          />
          <TextArea
            label="The Observation"
            hint="only afterward — what the story actually shows, and only what it can support"
            testId="input-observation"
            value={draft.observation}
            onChange={(v) => setDraft({ ...draft, observation: v })}
            minRows={4}
            optional
          />
          <ChangeField
            testId="input-change"
            value={draft.change}
            onChange={(v) => setDraft({ ...draft, change: v })}
          />
          <Field
            label="Tags"
            optional
            testId="input-tags"
            value={draft.tagsText}
            onChange={(v) => setDraft({ ...draft, tagsText: v })}
            placeholder="separated by commas"
          />

          <div className="pt-4 flex items-center gap-6">
            <button
              onClick={save}
              disabled={!canSave}
              data-testid="btn-save"
              className="text-[13px] tracking-wide transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ color: canSave ? "#1c1917" : "#a8a29e" }}
            >
              {saving ? "saving…" : "save this arrival"}
            </button>
            {justSavedTitle && (
              <span className="text-[12px] italic text-stone-400" data-testid="save-confirmation">
                Witnessed. Thank you.
              </span>
            )}
            {error && (
              <span className="text-[12px] text-red-400" data-testid="save-error">
                {error}
              </span>
            )}
          </div>
        </section>

        {/* Previously witnessed. */}
        <section className="mt-32" data-testid="entries-section">
          <h2
            className="text-stone-800"
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontWeight: 400,
              fontSize: "1.4rem",
              letterSpacing: "-0.005em",
            }}
          >
            Previously witnessed
          </h2>
          <div className="mt-2 text-[11px] uppercase tracking-[0.18em] text-stone-400">
            in the order in which they arrived
          </div>

          <div className="mt-10 space-y-16" data-testid="entries-list">
            {loading && (
              <div className="text-stone-400 italic text-[13px]" data-testid="entries-loading">
                gathering…
              </div>
            )}
            {!loading && entries.length === 0 && (
              <div className="text-stone-400 italic text-[13px]" data-testid="entries-empty">
                Nothing witnessed yet. The first arrival will begin the record.
              </div>
            )}
            {!loading &&
              entries.map((entry) =>
                editingId === entry.id ? (
                  <EditingEntry
                    key={entry.id}
                    entry={entry}
                    draft={editDraft}
                    setDraft={setEditDraft}
                    onSave={() => saveEdit(entry.id)}
                    onCancel={cancelEdit}
                  />
                ) : (
                  <Entry key={entry.id} entry={entry} onEdit={() => beginEdit(entry)} />
                ),
              )}
          </div>
        </section>
      </div>
    </div>
  );
}

function Field({ label, hint, optional, testId, value, onChange, placeholder }) {
  return (
    <div>
      <Label label={label} hint={hint} optional={optional} />
      <input
        type="text"
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-transparent outline-none border-b border-stone-200 focus:border-stone-500 py-2 text-stone-800 placeholder:text-stone-300 transition-colors"
        style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 300, fontSize: "1.05rem" }}
      />
    </div>
  );
}

function TextArea({ label, hint, optional, testId, value, onChange, minRows = 4 }) {
  return (
    <div>
      <Label label={label} hint={hint} optional={optional} />
      <textarea
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={minRows}
        className="w-full bg-transparent outline-none border-b border-stone-200 focus:border-stone-500 py-2 text-stone-800 placeholder:text-stone-300 leading-relaxed resize-y transition-colors"
        style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 300, fontSize: "1.05rem", lineHeight: 1.65 }}
      />
    </div>
  );
}

/**
 * ChangeField — quieter than Arrival and Observation. It should read
 * as a gentle invitation, not a required questionnaire. No bold
 * heading, no callout, no bordered box — just a small italic prompt
 * and a select styled as a bare underlined field.
 */
function ChangeField({ testId, value, onChange }) {
  return (
    <div className="pt-2">
      <div
        className="italic text-stone-400 mb-2"
        style={{ fontSize: "0.95rem", lineHeight: 1.55 }}
      >
        What changed because of this understanding?{" "}
        <span className="text-[10px] not-italic tracking-[0.18em] uppercase text-stone-300 ml-1">
          optional
        </span>
      </div>
      <select
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-transparent outline-none border-b border-stone-100 focus:border-stone-400 py-2 text-stone-600 transition-colors cursor-pointer"
        style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 300, fontSize: "1rem" }}
      >
        {CHANGE_OPTIONS.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function Label({ label, hint, optional }) {
  return (
    <div className="flex items-baseline gap-3 mb-2">
      <span className="text-[10px] uppercase tracking-[0.18em] text-stone-500">{label}</span>
      {optional && <span className="text-[10px] italic text-stone-300">optional</span>}
      {hint && <span className="text-[11px] italic text-stone-400">— {hint}</span>}
    </div>
  );
}

function Entry({ entry, onEdit }) {
  return (
    <article className="pl-6 border-l border-stone-100" data-testid={`entry-${entry.id}`}>
      <div className="flex items-baseline justify-between gap-4">
        <h3
          className="text-stone-900"
          style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontWeight: 400,
            fontSize: "1.55rem",
            lineHeight: 1.25,
            letterSpacing: "-0.005em",
          }}
          data-testid={`entry-title-${entry.id}`}
        >
          {entry.title}
        </h3>
        <button
          onClick={onEdit}
          data-testid={`entry-edit-${entry.id}`}
          className="text-[11px] italic text-stone-400 hover:text-stone-700 transition-colors flex-shrink-0"
        >
          revise
        </button>
      </div>
      <div className="mt-1 text-[11px] uppercase tracking-[0.18em] text-stone-400">
        {formatDate(entry.created_at)}
      </div>

      <div className="mt-5">
        <div className="text-[10px] uppercase tracking-[0.18em] text-stone-400 mb-2">The Arrival</div>
        <p
          className="text-stone-700 whitespace-pre-wrap"
          style={{ fontSize: "1rem", lineHeight: 1.7 }}
          data-testid={`entry-arrival-${entry.id}`}
        >
          {entry.arrival}
        </p>
      </div>

      {entry.observation && (
        <div className="mt-6">
          <div className="text-[10px] uppercase tracking-[0.18em] text-stone-400 mb-2">The Observation</div>
          <p
            className="text-stone-700 whitespace-pre-wrap"
            style={{ fontSize: "1rem", lineHeight: 1.7 }}
            data-testid={`entry-observation-${entry.id}`}
          >
            {entry.observation}
          </p>
        </div>
      )}

      {entry.change && (
        <div className="mt-6" data-testid={`entry-change-${entry.id}`}>
          <p
            className="italic text-stone-500"
            style={{ fontSize: "0.95rem", lineHeight: 1.6 }}
          >
            What changed:{" "}
            <span className="text-stone-700 not-italic">{entry.change}</span>
          </p>
        </div>
      )}

      {entry.tags && entry.tags.length > 0 && (
        <div className="mt-6 flex flex-wrap gap-3" data-testid={`entry-tags-${entry.id}`}>
          {entry.tags.map((t) => (
            <span key={t} className="text-[11px] italic text-stone-400">
              · {t}
            </span>
          ))}
        </div>
      )}
    </article>
  );
}

function EditingEntry({ entry, draft, setDraft, onSave, onCancel }) {
  return (
    <article className="pl-6 border-l border-amber-200 space-y-6" data-testid={`entry-editing-${entry.id}`}>
      <div className="text-[10px] uppercase tracking-[0.18em] text-stone-500">revising · {formatDate(entry.created_at)}</div>
      <Field label="Title" testId={`edit-title-${entry.id}`} value={draft.title} onChange={(v) => setDraft({ ...draft, title: v })} />
      <TextArea label="The Arrival" testId={`edit-arrival-${entry.id}`} value={draft.arrival} onChange={(v) => setDraft({ ...draft, arrival: v })} minRows={6} />
      <TextArea label="The Observation" optional testId={`edit-observation-${entry.id}`} value={draft.observation} onChange={(v) => setDraft({ ...draft, observation: v })} minRows={4} />
      <ChangeField testId={`edit-change-${entry.id}`} value={draft.change} onChange={(v) => setDraft({ ...draft, change: v })} />
      <Field label="Tags" optional testId={`edit-tags-${entry.id}`} value={draft.tagsText} onChange={(v) => setDraft({ ...draft, tagsText: v })} placeholder="separated by commas" />
      <div className="flex items-center gap-6 pt-2">
        <button
          onClick={onSave}
          data-testid={`edit-save-${entry.id}`}
          className="text-[13px] tracking-wide text-stone-900 hover:text-black transition-colors"
        >
          save the revision
        </button>
        <button
          onClick={onCancel}
          data-testid={`edit-cancel-${entry.id}`}
          className="text-[12px] italic text-stone-400 hover:text-stone-700 transition-colors"
        >
          cancel
        </button>
      </div>
    </article>
  );
}
